/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useRetracked } from 'js/lib/retracked';
import { get as getUser } from 'js/lib/user';

import { Typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import CoachIcon from 'bundles/ai-course-coach/assets/CoachIcon';
import QuickMessage from 'bundles/ai-course-coach/components/HomeView/QuickMessage';
import { firstNameFromFullName } from 'bundles/ai-course-coach/utils/nameParser';
import { useCoachTrackingContext } from 'bundles/ai-course-coach/utils/tracking';
import type { QuickMessage as QuickMessageType, SendMessageFunc } from 'bundles/ai-course-coach/utils/types';

import _t from 'i18n!nls/learning-assistant-chat-ai';

type Props = {
  title?: string; // No longer used but keeping in case we want to customize titles in the future
  subtitle?: string; // No longer used but keeping in case we want to customize subtitles in the future
  quickMessages?: QuickMessageType[];
  sendMessage: SendMessageFunc;
};

const styles = {
  heading: css`
    margin: 0 0 var(--cds-spacing-300) 0;
  `,
  icon: css`
    margin: var(--cds-spacing-300) 0;
    display: flex;
    justify-content: center;
    align-items: center;
  `,

  title: css`
    color: var(--cds-color-emphasis-primary-background-default);
  `,
  subtitle: css`
    margin: 0;
  `,
};

export const HomeView = ({ quickMessages, sendMessage }: Props) => {
  const trackRetracked = useRetracked();
  const track = useTracker();
  const trackingContext = useCoachTrackingContext()?.data;

  const { fullName: userName } = getUser();
  const userFirstName = userName ? firstNameFromFullName(userName) : null;
  const displayTitle = userFirstName ? _t('Hi, #{userFirstName}!', { userFirstName }) : _t('Hi, learner!');
  const displaySubtitle = _t('How can I help?');

  const homeViewQuickMessages = quickMessages || [];
  const homeViewTitle = displayTitle || _t("Ask me anything about what you're learning. I'm here to help!");
  const homeViewSubtitle =
    displaySubtitle || _t('To get started, type your question below or try one of these examples:');

  const coachIconToRender = <CoachIcon size="large" />;

  return (
    <div
      className="coach-home-view"
      css={css`
        padding: 0 var(--cds-spacing-300) var(--cds-spacing-300);
        height: auto;
        background-color: var(--cds-color-neutral-primary-invert);
        border-radius: 0 16px 16px 16px;
      `}
      aria-live="polite"
    >
      <div css={styles.heading}>
        <div css={styles.icon}>{coachIconToRender}</div>
        <Typography2 component="h3" variant="titleSmall" css={styles.title}>
          {homeViewTitle}
        </Typography2>
        <Typography2 component="p" variant="titleSmall" css={styles.subtitle}>
          {homeViewSubtitle}
        </Typography2>
      </div>
      {homeViewQuickMessages.map(({ message, action, onClick }) => (
        <QuickMessage
          key={message}
          message={message}
          onClick={() => {
            sendMessage(message, action);
            onClick?.();
            trackRetracked({
              trackingData: {
                example_text: message,
                timestamp: Date.now(),
              },
              trackingName: 'ai_course_coach_chat_quick_example',
              action: 'click',
            });

            if (trackingContext) {
              // v3 tracking
              track('interact_coach', {
                coachInteractionType: 'click_action',
                coach: {
                  mode: trackingContext.mode,
                  ...(trackingContext.location && { location: trackingContext.location }),
                },
                coachAction: action,
                ...(trackingContext.courseId && {
                  product: {
                    type: 'course',
                    id: trackingContext.courseId as string,
                  },
                }),
                ...(trackingContext.itemId && {
                  courseItem: {
                    id: trackingContext.itemId as string,
                  },
                }),
              });
            }
          }}
        />
      ))}
    </div>
  );
};

export default HomeView;
